/**
 * ============================================================================
 * ROLLING 180-DAY DECAL IMAGE CACHE UTILITY (decalCache.ts)
 * ============================================================================
 * Handles Service Worker registration and client-side Cache Storage tracking
 * so that all RandomThings images are cached for 180 days and renewed on every visit.
 * Any new image added under /Images/ is automatically tracked and cached.
 * ============================================================================
 */

const CACHE_NAME = 'schz-random-things-v1';
const DB_NAME = 'schz-cache-tracker';
const DB_VERSION = 1;
const STORE_NAME = 'rolling-cache';
const CACHE_TTL_MS = 180 * 24 * 60 * 60 * 1000; // 180 days in ms

let dbPromise: Promise<IDBDatabase> | null = null;

function getClientDB(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        return reject(new Error('IndexedDB not supported'));
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'url' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

/**
 * Register the Service Worker for automatic interception and 180-day rolling caching.
 */
export function initDecalCache(): void {
  if (typeof window === 'undefined') return;

  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((reg) => {
          if (reg.waiting) {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        })
        .catch((err) => {
          console.warn('[Cache] SW registration notice:', err);
        });
    });
  }
}

/**
 * Touch an image URL to renew its 180-day lifespan starting from this visit.
 * Works for ANY RandomThing picture automatically.
 */
export async function touchDecalImage(src: string): Promise<void> {
  if (typeof window === 'undefined' || !src) return;

  try {
    const fullUrl = new URL(src, window.location.origin).href;

    // 1. Notify active Service Worker if available
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'TOUCH_URL',
        url: fullUrl
      });
    }

    // 2. Also renew in IndexedDB from the client thread directly
    const db = await getClientDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const now = Date.now();
    store.put({
      url: fullUrl,
      lastAccessed: now,
      expiresAt: now + CACHE_TTL_MS
    });

    // 3. If window.caches is available, ensure the image is pre-warmed in CacheStorage
    if ('caches' in window) {
      const cache = await window.caches.open(CACHE_NAME);
      const match = await cache.match(fullUrl);
      if (!match) {
        // Pre-warm newly added image into cache
        fetch(fullUrl)
          .then((res) => {
            if (res.ok) cache.put(fullUrl, res);
          })
          .catch(() => {});
      }
    }
  } catch {
    // Non-blocking background operation
  }
}
